<template>
    <div>
        <div class="container image-form" ref="crop_reference">
            <div class="columns image-form-details">
                <div class="column is-narrow">
                    <div class="field">
                        <label class="label">
                            {{ create ? 'Select an image for upload' : 'Select an image to replace the old image' }}
                        </label>
                        <p class="control">
                            <input type="file" name="imageUpload" v-on:change="imageChanged">
                        </p>
                    </div>
                </div>
                <div class="column" v-if="imageReady">
                    <strong>Image Details</strong>
                    <br/>
                    {{ width }} x {{ height }}px
                    <div class="is-pulled-right">
                    {{ size | prettyBytes }} / 50 MB
                    </div>
                    <progress class="progress is-info"
                              :value="Math.round(size / 50000000 * 100)"
                              max="100">
                        {{ Math.round(size / 50000000 * 100) }}%
                    </progress>
                </div>
                <div class="column" v-if="imageReady">
                    <form-group type="text"
                                ref="image_name"
                                label="Image Name"
                                id="name"
                                v-model="name"
                                :error="nameError"
                                @input="nameChanged">
                    </form-group>
                </div>
            </div>
        </div>

        <hr/>
        <h1 v-if="loading" class="title has-text-centered"><strong>Loading image...</strong></h1>

        <div v-if="!loading && imageReady">
            <div v-if="step === 'crop'">
                <div class="container">
                    <h3 class="subtitle" v-if="preview !== null">
                        <div class="is-pulled-right">
                            <button type="button" class="button is-success" @click="nextStep('thumbnail')">
                                Select the thumbnail
                            </button>
                            <button type="button" class="button" @click="nextStep('finalize')">
                                Review
                                &nbsp;
                                <i class="fa fa-arrow-right"></i>
                            </button>
                        </div>
                        Main Image Crop
                        <br/>
                        <small><em>Crops the whole image for all sizes. Typically unnecessary for most images, however,
                        you can use it to remove large/unnecessary padding from the edges of the image or
                        you can use it to cutout a smaller area of the image.
                        </em></small>
                    </h3>
                </div>
                <image-cropper v-if="step === 'crop'"
                               :image-data="preview"
                               :start-crop="mainCrop"
                               :min-size="{width: 200, height: 200}"
                               @cropped="setMainCrop"></image-cropper>
            </div>
            <div v-if="step === 'thumbnail'">
                <div class="container">
                    <h3>
                        <div class="is-pulled-right">
                            <button type="button" class="button" @click="nextStep('crop')">
                                <i class="fa fa-arrow-left"></i>
                                &nbsp;
                                Edit Main Crop
                            </button>
                            <button type="button" class="button is-success" @click="nextStep('finalize')">
                                Review
                                &nbsp;
                                <i class="fa fa-arrow-right"></i>
                            </button>
                        </div>
                        Thumbnail Crop
                        <br/>
                        <small><em>Crops the area that will be used for the thumbnail.
                        The thumbnail crop can be used to enlarge details that would otherwise
                        be too small to see when the image is scaled down to the thumbnail's maximum size of 200x200px.
                        </em></small>
                    </h3>
                </div>
                <image-cropper v-if="step === 'thumbnail'"
                               :image-data="preview"
                               :start-crop="thumbCrop || mainCrop"
                               :bounds="mainCrop"
                               :min-size="{width: 50, height: 50}"
                               @cropped="setThumbCrop"></image-cropper>
            </div>
            <div v-if="step === 'finalize'">
                <div class="container">
                    <h3 class="is-clearfix">
                        <div class="is-pulled-right">
                            <button type="button" class="button" @click="nextStep('crop')">Edit Main Crop</button>
                            <button type="button" class="button" @click="nextStep('thumbnail')">Edit Thumbnail</button>
                            <button v-if="imageReady" type="button" class="button is-primary" @click="submit()">Upload</button>
                        </div>
                        Review the generated sizes.
                        <br/>
                        <small><em>Upload once you are satisfied with the results.</em></small>
                    </h3>

                    <h4>Thumbnail Size</h4>
                    <div class="crop-preview" :style="{
                        width: Math.round(thumbWidth * thumbRatio)+'px',
                        height: Math.round(thumbHeight * thumbRatio)+'px'
                    }">
                        <img :src="preview" :style="{
                            width: Math.round(width * thumbRatio)+'px',
                            height: Math.round(height * thumbRatio)+'px',
                            top: -Math.round(thumbTop * thumbRatio)+'px',
                            left: -Math.round(thumbLeft * thumbRatio)+'px'
                        }">
                    </div>

                    <br/>

                    <h4>Normal Size</h4>

                    <div class="crop-preview" :style="{
                        width: Math.round(mainWidth * normalRatio)+'px',
                        height: Math.round(mainHeight * normalRatio)+'px'
                    }">
                        <img :src="preview" :style="{
                            width: Math.round(width * normalRatio)+'px',
                            height: Math.round(height * normalRatio)+'px',
                            top: -Math.round(mainTop * normalRatio)+'px',
                            left: -Math.round(mainLeft * normalRatio)+'px'
                        }">
                    </div>

                    <br/>

                    <h4>Full Size <em v-if="mainWidth > container.width">(scaled to fit the page)</em></h4>

                    <div class="crop-preview" :style="{
                        width: Math.round(mainWidth * mainRatio)+'px',
                        height: Math.round(mainHeight * mainRatio)+'px'
                    }">
                        <img :src="preview" :style="{
                            width: Math.round(width * mainRatio)+'px',
                            height: Math.round(height * mainRatio)+'px',
                            top: -Math.round(mainTop * mainRatio)+'px',
                            left: -Math.round(mainLeft * mainRatio)+'px'
                        }">
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" ref="stylesheet/scss">
    .image-form {
        padding-top: 1rem;
    }

    .image-form-details {
        background: #EEE;
    }
</style>

<script>
    import Form from "../../helpers/Form"
    import {mapGetters} from "vuex"
    import {CREATE_IMAGE, UPDATE_IMAGE, CHECK_IMAGE_NAME} from "../../store/mutation-types"
    export default {
        mounted() {
            this.loadImage()
            this.getContainerSize()
            this.$bus.$on('resize', this.getContainerSize)
        },
        beforeDestroy() {
            this.$bus.$off('resize', this.getContainerSize)
        },
        props: {
            image: {
                type: [String, Object], default: () => {
                    return {
                        name: '',
                    }
                }
            },
            create: {type: [Boolean], default: false}
        },
        data() {
            return {
                step: 'crop',
                file: null,
                name: '',
                nameError: null,
                nameTimeout: null,
                size: 0,
                width: 0,
                height: 0,
                mainCrop: null,
                thumbCrop: null,
                preview: null,
                imageReady: !this.create,
                loading: false,
                container: {
                    width: 0,
                    height: 0,
                }
            }
        },
        computed: {
            normalRatio () {
                let width = this.mainCrop === null ? this.width : this.mainCrop.width
                return width > 600 ? 600 / width : 1
            },
            mainRatio () {
                let width = this.mainCrop === null ? this.width : this.mainCrop.width
                return width > this.container.width ? this.container.width / width : 1
            },
            mainTop () {
                return this.mainCrop === null ? 0 : this.mainCrop.top
            },
            mainLeft () {
                return this.mainCrop === null ? 0 : this.mainCrop.left
            },
            mainWidth () {
                return this.mainCrop === null ? this.width : this.mainCrop.width
            },
            mainHeight () {
                return this.mainCrop === null ? this.height : this.mainCrop.height
            },
            thumbRatio () {
                let width = this.thumbCrop === null ? this.mainCrop === null ? this.width : this.mainCrop.width : this.thumbCrop.width
                return width > 200 ? 200 / width : 1
            },
            thumbTop () {
                return this.thumbCrop === null ? this.mainCrop === null ? 0 : this.mainCrop.top : this.thumbCrop.top
            },
            thumbLeft () {
                return this.thumbCrop === null ? this.mainCrop === null ? 0 : this.mainCrop.left : this.thumbCrop.left
            },
            thumbWidth () {
                return this.thumbCrop === null ? this.mainCrop === null ? this.width : this.mainCrop.width : this.thumbCrop.width
            },
            thumbHeight () {
                return this.thumbCrop === null ? this.mainCrop === null ? this.height : this.mainCrop.height : this.thumbCrop.height
            },
        },
        methods: {
            nextStep(step) {
                this.step = ''
                setTimeout(() => {
                    this.step = step
                })
            },
            submit() {
                if (this.nameError !== null) {
                    alert('Please choose another name for this image');
                    return
                }
                let formData = new FormData()
                formData.append('file', this.file)
                formData.append('name', this.name)
                formData.append('mainCrop', JSON.stringify(this.mainCrop))
                formData.append('thumbCrop', JSON.stringify(this.thumbCrop))
                if (!this.create) {
                    formData.append('id', this.image.id)
                }
                this.$store.dispatch(this.create ? CREATE_IMAGE : UPDATE_IMAGE, formData)
                    .then(data => {
                        this.$emit('saved', data)
                    })
            },
            imageChanged(event) {
                this.loading = event.target.files[0];
                if (event.target.files[0]) {
                    this.loadImage(event.target.files[0])
                }
            },
            loadImage(file = null) {
                this.loading    = true
                this.step       = 'crop'
                this.preview    = null
                this.mainCrop   = null
                this.thumbCrop  = null
                this.imageReady = false
                // if no file was passed and either the full_path or normal_path is set for the passed image
                // load the passed image into the cropper using XHR to read the image as a base64 blob
                if (file === null && this.image.full_path || file === null && this.image.normal_path) {
                    this.file = null
                    this.name = this.image.name
                    if (this.image.full_path) {
                        file      = this.image.full_path
                        this.size = this.image.full_size
                    } else if (this.image.normal_path) {
                        file      = this.image.normal_path
                        this.size = this.image.normal_size
                    }
                    let xhr    = new XMLHttpRequest();
                    xhr.onload = () => {
                        file = xhr.response
                        this.processImage(file)
                    };
                    xhr.open('GET', file);
                    xhr.responseType = 'blob';
                    xhr.send();
                } else if (file !== null) {
                    this.name = file.name.substr(0, file.name.lastIndexOf('.'))
                    this.file = file
                    this.size = file.size
                    this.processImage(file)
                }

                // if still null something went wrong, abort
                if (file === null) {
                    this.loading = false
                }
            },
            processImage(file) {
                if (this.$refs.image_name) {
                    this.$refs.image_name.tempValue                    = this.name
                    this.$refs.image_name.$refs.form_control.tempValue = this.name
                }

                let reader = new FileReader();

                reader.onload = (e) => {
                    this.preview    = e.target.result;
                    this.loading    = false
                    this.imageReady = true

                    let image    = new Image();
                    image.onload = () => {
                        this.height  = image.height
                        this.width   = image.width
                        this.loading = false
                    }
                    image.src    = this.preview
                }

                reader.readAsDataURL(file);
            },
            setMainCrop(newCrop) {
                this.mainCrop  = newCrop
                this.thumbCrop = null
            },
            setThumbCrop(newCrop) {
                this.thumbCrop = newCrop
            },
            nameChanged(name) {
                if (this.nameTimeout !== null) clearTimeout(this.nameTimeout)
                if (name.length < 1) return
                this.nameTimeout = setTimeout(() => {
                    let payload = {name}
                    if (!this.create) {
                        payload.id = this.image.id
                    }
                    this.$store.dispatch(CHECK_IMAGE_NAME, payload)
                        .then(passed => {
                            this.nameError = (passed) ? null : ['This name is in use. Please choose another.']
                        })
                        .catch(error => {
                            this.nameError = null
                        })
                }, 1000)
            },
            getContainerSize() {
                let container         = this.$refs.crop_reference.getBoundingClientRect()
                this.container.width  = container.width
                this.container.height = container.height
            }
        }
    };
</script>



// WEBPACK FOOTER //
// ImageForm.vue?78a5a316